import {clamp} from '@shared/lib/utils/math_utils';

import {
  hexStringToRgbColor,
  hslColorToRgbColor,
  rgbColorToHexString,
  rgbColorToHslColor,
} from '@shared_web/theme/color_convertions';
import {RgbColor} from '@shared_web/theme/color_models';
import {textColorForContrast} from '@shared_web/theme/color_utils';
import {FrontendTheme} from '@shared_web/theme/theme_model';
import {background, borderColor, paddings} from '@shared_web/theme/theme_utils';

const DEFAULT_THEME_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_THEME_TEXT_COLOR = '#000000';

export interface CreateThemeOptions {
  accentColor: string;
  backgroundColor?: string; // Default: white
  textColor?: string; // Default: black
  dark?: boolean; // Default: true if `backgroundColor` is darker than `textColor`
  contrast?: number; // Default: the contrast between `backgroundColor` and `textColor`
}

let lastCreateThemeOptions: CreateThemeOptions | undefined;
export const getLastCreateThemeOptions = () => lastCreateThemeOptions;

/* eslint-disable @typescript-eslint/no-magic-numbers */
export function createTheme(opts: CreateThemeOptions, overrides?: FrontendTheme): FrontendTheme {
  lastCreateThemeOptions = opts;
  const {
    accentColor,
    backgroundColor = DEFAULT_THEME_BACKGROUND_COLOR,
    textColor = DEFAULT_THEME_TEXT_COLOR,
    dark: darkFromOpts,
    contrast: contrastFromOpts,
  } = opts;

  // const e = luminance(hexStringToRgbColor(backgroundColor));
  // const f = luminance(hexStringToRgbColor(textColor));
  // console.log('createTheme', {
  //   backgroundColor,
  //   textColor,
  //   e,
  //   f,
  // });

  const dark = darkFromOpts ?? false;
  const contrast = contrastFromOpts ?? 10;
  const backgroundTextColor = textColor;

  // Compute accent color
  const accentColorRgb = hexStringToRgbColor(accentColor);
  const accentColorHsl = rgbColorToHslColor(accentColorRgb);

  const withDark = (v: number) => (dark ? 1 / v : v);

  // Compute a highlighted version of the accent color for the hover state
  const {h, s, l, a} = accentColorHsl;
  const lHover = clamp(l * withDark(1.33));
  const accentColorHoverHsl = {h, s, l: lHover, a};
  const accentColorHoverRgb = hslColorToRgbColor(accentColorHoverHsl);
  const accentColorHover = rgbColorToHexString(accentColorHoverRgb);

  // Compute a desaturated version of the hover color for the disabled state
  const lDisabled = clamp(l * withDark(1.75));
  const sDisabled = clamp(s * 0.4);
  const accentColorDisabledHsl = {h, s: sDisabled, l: lDisabled, a};
  const accentColorDisabledRgb = hslColorToRgbColor(accentColorDisabledHsl);
  const accentColorDisabled = rgbColorToHexString(accentColorDisabledRgb);

  // Compute the best text color for each theme colors
  const accentTextColor = textColorForContrast(accentColorRgb, contrast);
  const accentTextColorHover = textColorForContrast(accentColorHoverRgb, contrast);
  const accentTextColorDisabled = textColorForContrast(accentColorDisabledRgb, contrast);

  // Compute the background colors
  const backgroundShadeRgb = (shade: number): RgbColor => {
    const v = dark ? shade : 1 - shade;
    return {r: v, g: v, b: v, a: 1};
  };
  const backgroundShade = (shade: number): string => rgbColorToHexString(backgroundShadeRgb(shade));

  //
  // Highlight with a transparent white (in dark mode) or black (in light mode)
  // Colors where manually written based on this test UI.
  //

  // <Flex $column $gap="xl" $padding="xl" $width="100%" $height="fit-content" $backgroundColor="#888">
  //   <Flex $justify="flex-start" $gap="m" $padding="m" $backgroundColor="#ffffff">
  //     <Flex $width={100} $height={100} $backgroundColor="#0000000D" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000014" />
  //     <Flex $width={100} $height={100} $backgroundColor="#0000001F" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000029" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000033" />
  //     <Flex $width={100} $height={100} $backgroundColor="#0000004D" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000066" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000080" />
  //     <Flex $width={100} $height={100} $backgroundColor="#00000099" />
  //     <Flex $width={100} $height={100} $backgroundColor="#000000B3" />
  //   </Flex>
  //   <Flex $justify="flex-start" $gap="m" $padding="m" $backgroundColor="#000000">
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff1A" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff26" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff33" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff40" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff4D" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff66" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff80" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffff99" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffffB3" />
  //     <Flex $width={100} $height={100} $backgroundColor="#ffffffCC" />
  //   </Flex>
  // </Flex>
  const highlight1 = dark ? '#ffffff1a' : '#0000000d';
  const highlight2 = dark ? '#ffffff26' : '#00000014';
  const highlight3 = dark ? '#ffffff33' : '#0000001f';
  const highlight4 = dark ? '#ffffff40' : '#00000029';
  const highlight5 = dark ? '#ffffff4d' : '#00000033';
  const highlight6 = dark ? '#ffffff66' : '#0000004d';
  const highlight7 = dark ? '#ffffff80' : '#00000066';
  const highlight8 = dark ? '#ffffff99' : '#00000080';
  const highlight9 = dark ? '#ffffffb3' : '#00000099';
  const highlight10 = dark ? '#ffffffcc' : '#000000b3';

  // Base styling for buttons
  const buttonBase = {
    textColorLoading: 'transparent',
    textDecoration: undefined,
    ...borderColor(undefined),
    borderWidth: 0,
    fontFamily: undefined,
    fontWeight: 500,
    lineHeight: 1.75,
    letterSpacing: undefined,
    textUnderlineOffset: 3,
  } as const;

  // Base styling for textfields
  const baseTextfield = {
    backgroundColorActive: backgroundColor,
    backgroundColorHover: backgroundColor,
    backgroundColorFocus: backgroundColor,
    backgroundColorDisabled: backgroundShade(0.08),
    borderColor: `${accentColor}99`,
    hoverBorderColor: `${accentColor}dd`,
    focusBorderColor: accentColor,
    textColor: backgroundTextColor,
    textColorDisabled: textColorForContrast(backgroundShadeRgb(0.08), 3),
    focusTextColor: backgroundTextColor,
    borderRadius: 6,
    focusOutlineColor: `${accentColor}33`,
    focusOutlineWidth: 4,
    borderWidth: 1,
    focusBorderWidth: 1,
    fontSize: 16,
    fontFamily: undefined,
    fontWeight: 400,
    labelMarginBottom: '0.4em',
    labelTextColor: backgroundTextColor,
  } as const;

  const theme = {
    main: {
      dark,
      backgroundColor,
      textColor: backgroundTextColor,
      accentColor,
      accentTextColor,
      highlight1,
      highlight2,
      highlight3,
      highlight4,
      highlight5,
      highlight6,
      highlight7,
      highlight8,
      highlight9,
      highlight10,
      // Based on Ant Design
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'`,
      fontSize: 14,
      lineHeight: '22px',
      ...overrides?.main,
    },
    button: {
      ...buttonBase,
      textColorActive: accentTextColor,
      textColorDisabled: accentTextColorDisabled,
      textColorHover: accentTextColorHover,
      textDecorationHover: undefined,
      backgroundColorActive: accentColor,
      backgroundColorDisabled: accentColorDisabled,
      backgroundColorHover: accentColorHover,
      backgroundColorLoading: accentColorDisabled,
      focusBorderColor: '#ef8b59',
      focusBorderWidth: 2,
      focusTextDecoration: undefined,
      paddingTop: 8,
      paddingRight: 16,
      paddingBottom: 8,
      paddingLeft: 16,
      borderRadius: 6,
      fontSize: 15,
      enableSelect: false,
      loaderColor: accentTextColor,
      loaderOpacity: 1,
      loaderSize: 24,
      ...overrides?.button,
    },
    link: {
      ...buttonBase,
      textColorActive: accentColor,
      textColorDisabled: accentColorDisabled,
      textColorLoading: accentColorDisabled,
      textColorHover: accentColor,
      textDecorationHover: 'underline',
      ...background('transparent'),
      focusBorderColor: undefined,
      focusBorderWidth: undefined,
      focusTextDecoration: 'underline',
      ...paddings(0),
      borderRadius: undefined,
      fontSize: undefined,
      enableSelect: true,
      loaderColor: accentColor,
      loaderOpacity: 0.3,
      loaderSize: 20,
      ...overrides?.link,
    },
    checkbox: {
      labelPaddingTop: 8,
      labelPaddingRight: 8,
      labelPaddingBottom: 8,
      labelPaddingLeft: 8,
      labelBorderRadius: 6,
      labelHoverColor: highlight2,
      size: undefined,
      backgroundColor: 'transparent',
      backgroundColorChecked: accentColor,
      borderColor: accentColor,
      borderColorChecked: accentColor,
      borderRadius: 4,
      borderWidth: 2,
      marginRight: 8,
      ...overrides?.checkbox,
    },
    radio: {
      color: backgroundTextColor,
      radioColor: accentColor,
      fontSize: 16,
      fontWeight: 500,
      labelPaddingTop: 8,
      labelPaddingRight: 8,
      labelPaddingBottom: 8,
      labelPaddingLeft: 8,
      labelBorderRadius: 6,
      labelHoverColor: highlight2,
      size: undefined,
      titleMarginBottom: '0.4em',
      inputHeight: undefined,
      ...overrides?.radio,
    },
    input: {
      ...baseTextfield,
      paddingRight: 16,
      paddingLeft: 16,
      height: 48,
      ...overrides?.input,
    },
    textarea: {
      ...baseTextfield,
      paddingTop: 8,
      paddingRight: 16,
      paddingBottom: 8,
      paddingLeft: 16,
      ...overrides?.textarea,
    },
  };

  return theme;
}
/* eslint-enable @typescript-eslint/no-magic-numbers */
